import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import AdminLayout from '../../components/admin/AdminLayout';
import { getCourseModules, createCourseModule, updateCourseModule, deleteCourseModule, getAdminCourse } from '../../api/client';
import { useToast } from '../../contexts/ToastContext';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { ArrowLeft, Plus, Trash2, GripVertical, Loader2, Save, X, BookOpen } from 'lucide-react';

interface Module {
  id: number;
  title: string;
  description: string;
  content: string;
  video_url: string;
  is_free: number;
  sort_order: number;
}

export default function CourseModules(): React.JSX.Element {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const [course, setCourse] = useState<any>(null);
  const [modules, setModules] = useState<Module[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingModule, setEditingModule] = useState<Module | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    title: '',
    description: '',
    content: '',
    video_url: '',
    is_free: false,
    sort_order: 0,
  });

  useEffect(() => {
    loadData();
  }, [courseId]);

  const loadData = async () => {
    if (!courseId) return;
    setLoading(true);
    try {
      const [courseRes, modulesRes] = await Promise.all([
        getAdminCourse(Number(courseId)),
        getCourseModules(Number(courseId)),
      ]);
      setCourse(courseRes.course);
      setModules(modulesRes.modules || []);
    } catch (err: any) {
      toast.show(err.message || 'Failed to load data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingModule(null);
    setForm({ title: '', description: '', content: '', video_url: '', is_free: false, sort_order: modules.length });
    setShowForm(true);
  };

  const openEdit = (mod: Module) => {
    setEditingModule(mod);
    setForm({
      title: mod.title,
      description: mod.description || '',
      content: mod.content || '',
      video_url: mod.video_url || '',
      is_free: !!mod.is_free,
      sort_order: mod.sort_order,
    });
    setShowForm(true);
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) {
      toast.show('Title is required', 'error');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, is_free: form.is_free ? 1 : 0 };
      if (editingModule) {
        await updateCourseModule(Number(courseId), editingModule.id, payload);
        toast.show('Module updated', 'success');
      } else {
        await createCourseModule(Number(courseId), payload);
        toast.show('Module created', 'success');
      }
      setShowForm(false);
      loadData();
    } catch (err: any) {
      toast.show(err.message || 'Failed to save module', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (mod: Module) => {
    if (!confirm(`Delete module "${mod.title}"?`)) return;
    try {
      await deleteCourseModule(Number(courseId), mod.id);
      setModules((prev) => prev.filter((m) => m.id !== mod.id));
      toast.show('Module deleted', 'success');
    } catch (err: any) {
      toast.show(err.message || 'Failed to delete', 'error');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate('/admin/courses')}
            className="p-2 rounded-xl hover:bg-slate-100 text-slate-500 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Curriculum Builder</h1>
            <p className="text-sm text-slate-500">{course?.title}</p>
          </div>
        </div>

        <div className="flex justify-between items-center mb-4">
          <p className="text-sm text-slate-500">{modules.length} module{modules.length !== 1 ? 's' : ''}</p>
          <Button size="sm" onClick={openCreate} leftIcon={<Plus className="w-4 h-4" />}>
            Add Module
          </Button>
        </div>

        {showForm && (
          <Card className="mb-6">
            <h3 className="font-semibold text-slate-900 mb-4">
              {editingModule ? 'Edit Module' : 'New Module'}
            </h3>
            <div className="space-y-3">
              <Input
                label="Title"
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              />
              <Input
                label="Description"
                multiline
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              />
              <Input
                label="Video URL"
                value={form.video_url}
                onChange={(e) => setForm((f) => ({ ...f, video_url: e.target.value }))}
              />
              <Input
                label="Content"
                multiline
                value={form.content}
                onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))}
              />
              <div className="grid grid-cols-2 gap-3">
                <Input
                  label="Sort Order"
                  type="number"
                  value={String(form.sort_order)}
                  onChange={(e) => setForm((f) => ({ ...f, sort_order: Number(e.target.value) }))}
                />
                <div className="flex items-center gap-2 pt-6">
                  <input
                    type="checkbox"
                    id="is_free"
                    checked={form.is_free}
                    onChange={(e) => setForm((f) => ({ ...f, is_free: e.target.checked }))}
                    className="w-4 h-4 rounded border-slate-300 text-amber-500"
                  />
                  <label htmlFor="is_free" className="text-sm text-slate-700">Free Preview</label>
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <Button size="sm" onClick={handleSubmit} isLoading={saving} leftIcon={<Save className="w-4 h-4" />}>
                  Save
                </Button>
                <Button size="sm" variant="secondary" onClick={() => setShowForm(false)} leftIcon={<X className="w-4 h-4" />}>
                  Cancel
                </Button>
              </div>
            </div>
          </Card>
        )}

        <div className="space-y-3">
          {modules.length === 0 ? (
            <Card className="text-center py-12 text-slate-500">
              <BookOpen className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              <p>No modules yet. Add your first lesson above.</p>
            </Card>
          ) : (
            modules.map((mod, idx) => (
              <Card key={mod.id} className="flex items-start gap-3">
                <div className="pt-1 text-slate-400">
                  <GripVertical className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-amber-500 bg-amber-50 px-2 py-0.5 rounded-full">
                      {idx + 1}
                    </span>
                    <h3 className="font-semibold text-slate-900">{mod.title}</h3>
                    {mod.is_free ? (
                      <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-0.5 rounded-full">Free</span>
                    ) : null}
                  </div>
                  {mod.description && <p className="text-sm text-slate-500 mt-1">{mod.description}</p>}
                  {mod.video_url && (
                    <p className="text-xs text-amber-500 mt-1 truncate">{mod.video_url}</p>
                  )}
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => openEdit(mod)}
                    className="p-1.5 rounded-lg text-slate-400 hover:text-amber-500 hover:bg-amber-50 transition-colors"
                  >
                    <Save className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(mod)}
                    className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </Card>
            ))
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
